import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'

// Within this many px of the bottom counts as "following the thread".
const BOTTOM_THRESHOLD = 100

/**
 * Keeps a scroll container pinned to its bottom while the reader is following
 * along, and lets go the moment they scroll up.
 *
 * Built on a callback ref plus a ResizeObserver rather than effects, because
 * both fire before the browser paints: the container is pinned on the very
 * first painted frame, and every later growth (new messages, images finishing
 * decode, the composer or keyboard shrinking the list) re-pins invisibly.
 *
 * `resetKey` re-arms the pin — pass the id of whatever the list is showing,
 * since the route component is reused across a param change.
 */
export function useStickToBottom<T extends HTMLElement = HTMLDivElement>(resetKey?: unknown) {
  const elRef = useRef<T | null>(null)
  // Reader intent. A ref, not state: if the observer callback closed over
  // state, every stick/unstick would re-subscribe it and the pin would lag.
  const stickRef = useRef(true)
  const pinnedRef = useRef(false)
  const [container, setContainer] = useState<T | null>(null)
  const [content, setContent] = useState<HTMLElement | null>(null)
  const [isAtBottom, setIsAtBottom] = useState(true)

  const pin = useCallback(() => {
    const el = elRef.current
    if (!el) return
    el.scrollTop = el.scrollHeight
    pinnedRef.current = true
  }, [])

  // A callback ref, not useRef: the container can mount well after the first
  // render (lists usually sit behind a loading skeleton), so we react to the
  // node actually arriving instead of guessing when it will.
  const containerRef = useCallback((el: T | null) => {
    elRef.current = el
    setContainer(el)
    if (el) {
      el.scrollTop = el.scrollHeight
      pinnedRef.current = true
    }
  }, [])

  const contentRef = useCallback((el: HTMLElement | null) => setContent(el), [])

  // One observer for both boxes: the container shrinks (keyboard lift, a
  // growing composer) and the content grows (new items, images decoding).
  useEffect(() => {
    if (!container) return
    const ro = new ResizeObserver(() => {
      if (stickRef.current) pin()
    })
    ro.observe(container)
    if (content) ro.observe(content)
    // WebKit can settle layout a frame late behind a view transition.
    const raf = requestAnimationFrame(() => {
      if (stickRef.current) pin()
    })
    return () => {
      ro.disconnect()
      cancelAnimationFrame(raf)
    }
  }, [container, content, pin])

  // Layout, not passive: switching to another list reuses the same DOM node
  // (the route component is not remounted on a param change), so this is the
  // only pin that happens, and it has to land before the browser paints.
  useLayoutEffect(() => {
    stickRef.current = true
    pinnedRef.current = false
    setIsAtBottom(true)
    pin()
  }, [resetKey, pin])

  const onScroll = useCallback(() => {
    const el = elRef.current
    // Ignore scroll noise emitted before we have had our first say.
    if (!el || !pinnedRef.current) return
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < BOTTOM_THRESHOLD
    stickRef.current = atBottom
    setIsAtBottom(atBottom)
  }, [])

  /** Re-arm and pin now, e.g. right after sending. */
  const stickToBottom = useCallback(() => {
    stickRef.current = true
    setIsAtBottom(true)
    pin()
  }, [pin])

  return { containerRef, contentRef, onScroll, isAtBottom, stickToBottom }
}
